import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChevronRight, ChevronDown, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DateFilter from "@/components/DateFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BADGE = {
  debtor: "bg-blue-50 text-blue-700 border-blue-200",
  creditor: "bg-orange-50 text-orange-700 border-orange-200",
  branch: "bg-amber-50 text-amber-700 border-amber-200",
  related: "bg-violet-50 text-violet-700 border-violet-200",
  other: "bg-slate-50 text-slate-600 border-slate-200",
};

function fmt(n) {
  if (n === null || n === undefined) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Math.abs(n));
}

function fmtMonth(m) {
  const [y, mm] = m.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10) - 1]}-${y.slice(2)}`;
}

function DetailRow({ row }) {
  // row.items (inventory) OR row.ledger_breakdown
  const details = row.items || row.ledger_breakdown || [];
  const isInventory = !!row.items;
  return (
    <div className="p-4 pl-10">
      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
        {isInventory ? "Items" : "Expense Ledgers"}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-slate-500">
            <th className="text-left font-medium py-1">{isInventory ? "Item" : "Ledger"}</th>
            {isInventory && <th className="text-right font-medium py-1">Qty</th>}
            <th className="text-right font-medium py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          {details.map((d) => (
            <tr key={d.item || d.ledger}>
              <td className="py-1">{d.item || d.ledger}</td>
              {isInventory && <td className="text-right font-mono tabular-nums py-1">{fmt(d.qty)}</td>}
              <td className="text-right font-mono tabular-nums py-1">{fmt(d.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PartyRow({ party, months, testIdPrefix }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = (party.items?.length || party.ledger_breakdown?.length) > 0;
  const typeClass = TYPE_BADGE[party.party_type] || TYPE_BADGE.other;

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-slate-50"
        onClick={() => hasDetail && setExpanded(!expanded)}
        data-testid={`${testIdPrefix}-row-${(party.party || "").replace(/\s+/g, "-").toLowerCase()}`}
      >
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {hasDetail ? (expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />) : <span className="w-4" />}
            <span>{party.party}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${typeClass}`}>
              {party.party_type}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-center font-mono tabular-nums text-xs">{party.count}</TableCell>
        {months.map((m) => (
          <TableCell key={m} className="text-right font-mono tabular-nums text-xs">
            {party.monthly[m] > 0 ? fmt(party.monthly[m]) : "—"}
          </TableCell>
        ))}
        <TableCell className="text-right font-mono tabular-nums font-semibold text-slate-900">
          {fmt(party.total)}
        </TableCell>
      </TableRow>
      {expanded && hasDetail && (
        <TableRow className="bg-slate-50">
          <TableCell colSpan={months.length + 3} className="p-0">
            <DetailRow row={party} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Reusable Party Pivot Report
 * endpoint: 'party-sales' | 'party-purchase' | 'party-expense'
 * title, subtitle, partyTypeOptions[], totalColor
 */
export default function PartyPivotReport({
  companyContext, endpoint, title, subtitle,
  partyTypeOptions, totalColor = "text-slate-900", testIdPrefix = "party",
}) {
  const [data, setData] = useState({ parties: [], months: [], monthly_totals: {}, grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [partyType, setPartyType] = useState("all");
  const [downloading, setDownloading] = useState(false);

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters, company_id: companyId, party_type: partyType,
      };
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(`${API}/reports/${endpoint}`, { params });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [filters, companyId, branchId, partyType, endpoint]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = {
        ...filters, company_id: companyId, party_type: partyType,
      };
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(`${API}/reports/${endpoint}/export`, {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${endpoint.replace(/-/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading && data.parties.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  const { parties, months, monthly_totals, grand_total } = data;
  const totalTransactions = parties.reduce((s, p) => s + (p.count || 0), 0);

  return (
    <div data-testid={testIdPrefix}>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
        <Button
          onClick={handleDownload} disabled={downloading}
          variant="outline"
          className="border-slate-300"
          data-testid={`${testIdPrefix}-download`}
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? "Exporting..." : "Download Excel"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <DateFilter onFilterChange={setFilters} />
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
          {partyTypeOptions.map((pt) => (
            <button
              key={pt.value}
              onClick={() => setPartyType(pt.value)}
              data-testid={`${testIdPrefix}-type-${pt.value}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                partyType === pt.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {pt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Parties</p>
            <p className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums">{parties.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Transactions</p>
            <p className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums">{totalTransactions}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Grand Total</p>
            <p className={`text-xl font-bold mt-1 font-mono tabular-nums ${totalColor}`}>₹ {fmt(grand_total)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">
            {partyTypeOptions.find(p => p.value === partyType)?.label || "All"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Party Name</TableHead>
                <TableHead className="text-center font-semibold w-20">Txn</TableHead>
                {months.map((m) => (
                  <TableHead key={m} className="text-right font-semibold w-28 text-xs">
                    {fmtMonth(m)}
                  </TableHead>
                ))}
                <TableHead className="text-right font-semibold w-36 bg-slate-100">Total (₹)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.map((p) => (
                <PartyRow key={p.party} party={p} months={months} testIdPrefix={testIdPrefix} />
              ))}
              {parties.length > 0 && (
                <TableRow className="bg-slate-100 font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-center font-mono tabular-nums text-xs">{totalTransactions}</TableCell>
                  {months.map((m) => (
                    <TableCell key={m} className="text-right font-mono tabular-nums text-xs">
                      {fmt(monthly_totals[m] || 0)}
                    </TableCell>
                  ))}
                  <TableCell className={`text-right font-mono tabular-nums ${totalColor}`}>
                    {fmt(grand_total)}
                  </TableCell>
                </TableRow>
              )}
              {parties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={months.length + 3} className="text-center text-slate-500 py-8">
                    No data for this filter
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
